import { AsyncPipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, effect, inject, input, model, output, signal, ViewChild } from '@angular/core';
import { dsDtos } from '@vectronic/openApi-admin';
import { BehaviorSubject, debounceTime, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import {
  ChipsAutocompleteComponent,
  Item,
} from '../../components/chips-autocomplete/chips-autocomplete.component';
import { FilterHeaderComponent } from '../filter-header/filter-header.component';

@Component({
  selector: 'vectronic-process-id-order-filter',
  templateUrl: './process-id-order-filter.component.html',
  styleUrls: ['./process-id-order-filter.component.scss'],
  imports: [FilterHeaderComponent, ChipsAutocompleteComponent, AsyncPipe],
})
export class ProcessIdOrderFilterComponent {
  httpClient = inject(HttpClient);

  oofDbServicePath = input.required<string>();
  selectedFilterItems = model<Item<dsDtos.Order[]>[]>(undefined);
  selectedOrderItemConfigurationIds = output<string[]>();

  @ViewChild(ChipsAutocompleteComponent)
  private chipsAutocompleteComponent: ChipsAutocompleteComponent<any>;

  isFilterHidden = signal(true);
  filterText = new BehaviorSubject('');

  // noinspection JSUnusedGlobalSymbols
  setFilterActiveWhenSelectedItemIsValid = effect(() => {
    if (this.selectedFilterItems() != undefined) {
      this.isFilterHidden.set(false);
    }
  });

  // noinspection JSUnusedGlobalSymbols
  focusFilterInputOnShow = effect(() => {
    if (!this.isFilterHidden() && this.selectedFilterItems() === undefined) {
      this.chipsAutocompleteComponent?.focusInput();
    }
  });

  // noinspection JSUnusedGlobalSymbols
  emitSelectedOrderItemConfigurationIds = effect(() => {
    const selectedFilterItems = this.selectedFilterItems();
    if (!selectedFilterItems || selectedFilterItems.length == 0) {
      this.selectedOrderItemConfigurationIds.emit(undefined);
    } else {
      this.selectedOrderItemConfigurationIds.emit(
        selectedFilterItems.flatMap((item) => {
          const configurationIds = item.value.flatMap((order) =>
            (order.orderItems ?? []).flatMap((orderItem) =>
              (orderItem.orderItemConfigurations ?? []).map((config) => config.id),
            ),
          );
          return configurationIds.length > 0 ? configurationIds : [item.name];
        }),
      );
    }
  });

  availableFilterItems$: Observable<Item<dsDtos.Order[]>[]> = this.filterText.pipe(
    debounceTime(500),
    switchMap((text) => {
      if (text?.length > 1) {
        return this.httpClient.get<dsDtos.Order[]>(
          `${this.oofDbServicePath()}/admin/orders/byProcessIdContains`,
          {
            params: new HttpParams().set('fakturaIdSubstring', text),
          },
        );
      } else {
        return of<dsDtos.Order[]>([]);
      }
    }),
    map((orders) => {
      const items: Item<dsDtos.Order[]>[] = [];
      orders.forEach((order) => {
        const item = items.find((i) => i.name === order.fakturaId);
        if (item) {
          item.value.push(order);
        } else {
          items.push({
            name: order.fakturaId,
            value: [order],
          });
        }
      });
      return items;
    }),
    shareReplay(1),
  );

  toggleFilter() {
    this.isFilterHidden.update((pre) => !pre);
    this.selectedFilterItems.set(undefined);
  }

  onSelectFilterItem(selectedItem: Item<dsDtos.Order[]>) {
    this.selectedFilterItems.update((pre) => {
      if (pre === undefined) {
        return [selectedItem];
      }
      return pre.concat(selectedItem);
    });
  }

  onDeleteFilterItem(deletedItem: Item<dsDtos.Order[]>) {
    this.selectedFilterItems.update((pre) =>
      pre.filter((item) => item.value !== deletedItem.value),
    );
  }

  filterTextChanged(text: string) {
    this.filterText.next(text);
  }
}
